/**
 * Die VBA-Prüfmakros des Word-Kurses.
 *
 * Zwei Arten von Makros stehen hier:
 *
 * - Ersatzwege. `MAKRO_DOKUMENT` und `MAKRO_KORREKTUR` lesen Angaben aus,
 *   die Office.js zwar kennt, aber nur über WordApiDesktop 1.3/1.4. Word 2019
 *   und 2021 haben diesen Satz nicht. Dort springt das Makro ein.
 * - Einziger Weg. Seriendruck, Tabstopps, Seitenumbruch oberhalb,
 *   Seitenfarbe, Initiale, SmartArt und Diagramme, Schnellbausteine,
 *   AutoKorrektur, PDF-Fassung und aufgezeichnete Makros kommen in Office.js
 *   gar nicht vor.
 *
 * Mehrere Aufgaben teilen sich ein Makro: Wer „Dokument auslesen“ einmal in
 * ein Modul eingefügt hat, startet es für die nächste Aufgabe nur neu.
 */

import { pruefmakro } from "@/office/makro/vba";

/** Layout, Zeichen, Sprache, Formen und Ansicht in einem Durchgang. */
export const MAKRO_DOKUMENT = pruefmakro({
  pruefung: "word-dokument",
  host: "Word",
  titel: "Dokument auslesen",
  rumpf: [
    "Dim s As Section, p As Paragraph, sh As Shape",
    "Dim spaltenMax As Long, zeilennr As Boolean, ersteAnders As Boolean, seitenrahmen As Boolean",
    "Dim kap As Long, gross As Long, mitRahmen As Long, mitSchatten As Long, ohnePruefung As Long",
    "Dim ids(1 To 20) As Long, anz(1 To 20) As Long, n As Long, k As Long, lid As Long, bekannt As Boolean",
    "",
    "' Seite und Abschnitte",
    "spaltenMax = 1",
    "For Each s In ActiveDocument.Sections",
    "    If s.PageSetup.TextColumns.Count > spaltenMax Then spaltenMax = s.PageSetup.TextColumns.Count",
    "    If s.PageSetup.LineNumbering.Active <> 0 Then zeilennr = True",
    "    If s.PageSetup.DifferentFirstPageHeaderFooter <> 0 Then ersteAnders = True",
    "    If s.Borders.Enable <> 0 Then seitenrahmen = True",
    "Next s",
    'Zahl "spalten", spaltenMax',
    'JaNein "zeilennummern", zeilennr',
    'JaNein "ersteSeiteAnders", ersteAnders',
    'JaNein "seitenrahmen", seitenrahmen',
    'Zahl "seiteBreite", ActiveDocument.Sections(1).PageSetup.PageWidth',
    'Zahl "seiteHoehe", ActiveDocument.Sections(1).PageSetup.PageHeight',
    'JaNein "silbentrennung", ActiveDocument.AutoHyphenation',
    "",
    "' Absätze: Kapitälchen, Rahmen, Schattierung, Sprache",
    "For Each p In ActiveDocument.Paragraphs",
    "    If Len(p.Range.Text) > 1 Then",
    "        If p.Range.Font.SmallCaps <> 0 Then kap = kap + 1",
    "        If p.Range.Font.AllCaps <> 0 Then gross = gross + 1",
    "        If p.Borders.Enable <> 0 Then mitRahmen = mitRahmen + 1",
    "        If p.Shading.BackgroundPatternColor <> wdColorAutomatic Then mitSchatten = mitSchatten + 1",
    "        If p.Range.NoProofing <> 0 Then ohnePruefung = ohnePruefung + 1",
    "        lid = p.Range.LanguageID",
    "        bekannt = False",
    "        For k = 1 To n",
    "            If ids(k) = lid Then",
    "                anz(k) = anz(k) + 1",
    "                bekannt = True",
    "            End If",
    "        Next k",
    "        If Not bekannt And n < 20 Then",
    "            n = n + 1",
    "            ids(n) = lid",
    "            anz(n) = 1",
    "        End If",
    "    End If",
    "Next p",
    'Zahl "kapitaelchen", kap',
    'Zahl "grossbuchstaben", gross',
    'Zahl "absaetzeMitRahmen", mitRahmen',
    'Zahl "absaetzeMitSchattierung", mitSchatten',
    'Zahl "absaetzeOhnePruefung", ohnePruefung',
    'Zahl "sprachen", n',
    "For k = 1 To n",
    '    Zahl "sprache." & k & ".id", ids(k)',
    '    Zahl "sprache." & k & ".absaetze", anz(k)',
    "Next k",
    "",
    "' Frei positionierte Formen und ihr Textumbruch",
    "n = 0",
    "For Each sh In ActiveDocument.Shapes",
    "    n = n + 1",
    '    Zahl "form." & n & ".typ", sh.Type',
    '    Zahl "form." & n & ".umbruch", sh.WrapFormat.Type',
    "Next sh",
    'Zahl "formen", n',
    'Zahl "inlineBilder", ActiveDocument.InlineShapes.Count',
    "",
    "' Dokumentschutz (Bearbeitung einschränken)",
    'Zahl "schutz", ActiveDocument.ProtectionType',
    "",
    "' Ansicht des Fensters",
    'Zahl "ansicht", ActiveWindow.View.Type',
    'JaNein "navigationsbereich", ActiveWindow.DocumentMap',
    'JaNein "formatierungszeichen", ActiveWindow.View.ShowAll',
  ],
});

/** Kennzahlen aus `MAKRO_DOKUMENT`: VBA-Konstanten für Ansicht und Umbruch. */
export const VBA_ANSICHT: Record<number, string> = {
  1: "Normal",
  2: "Outline",
  3: "Print",
  4: "PrintPreview",
  5: "Master",
  6: "Web",
  7: "Reading",
};

export const VBA_UMBRUCH: Record<number, string> = {
  0: "Square",
  1: "Tight",
  2: "Through",
  3: "Front",
  4: "TopBottom",
  5: "Behind",
  7: "Inline",
};

/** `Shape.Type` aus VBA (MsoShapeType) für die Formen, die der Kurs unterscheidet. */
export const VBA_FORMTYP: Record<number, string> = {
  1: "GeometricShape",
  6: "Group",
  13: "Picture",
  17: "TextBox",
  20: "Canvas",
};

export { vbaSprache } from "@/office/makro/sprache";

/** Rechtschreib- und Grammatikfehler sowie die Lesbarkeitsstatistik. */
export const MAKRO_KORREKTUR = pruefmakro({
  pruefung: "word-korrektur",
  host: "Word",
  titel: "Rechtschreibung und Lesbarkeit auslesen",
  rumpf: [
    "Dim statistik As Object, k As Long",
    'Zahl "rechtschreibfehler", ActiveDocument.SpellingErrors.Count',
    'Zahl "grammatikfehler", ActiveDocument.GrammaticalErrors.Count',
    "",
    "' Die Lesbarkeitsstatistik löst eine Grammatikprüfung aus und kann etwas dauern.",
    "Set statistik = ActiveDocument.Content.ReadabilityStatistics",
    'Zahl "lesbarkeit", statistik.Count',
    "For k = 1 To statistik.Count",
    '    Wert "lesbarkeit." & k & ".name", statistik(k).Name',
    '    Zahl "lesbarkeit." & k & ".wert", statistik(k).Value',
    "Next k",
  ],
});

/** Tabstopps und die Behelfslösung mit mehreren Tabulatoren hintereinander. */
export const MAKRO_TABSTOPPS = pruefmakro({
  pruefung: "word-tabstopps",
  host: "Word",
  titel: "Tabstopps auslesen",
  rumpf: [
    "Dim p As Paragraph, k As Long",
    "Dim mitTabstopp As Long, mitFuellzeichen As Long, rechtsbuendig As Long, dezimal As Long",
    "Dim mehrfachTab As Long, mitTab As Long",
    "For Each p In ActiveDocument.Paragraphs",
    "    If InStr(p.Range.Text, vbTab) > 0 Then mitTab = mitTab + 1",
    "    If InStr(p.Range.Text, vbTab & vbTab) > 0 Then mehrfachTab = mehrfachTab + 1",
    "    If p.TabStops.Count > 0 Then",
    "        mitTabstopp = mitTabstopp + 1",
    "        For k = 1 To p.TabStops.Count",
    "            If p.TabStops(k).Leader <> wdTabLeaderSpaces Then",
    "                mitFuellzeichen = mitFuellzeichen + 1",
    "                Exit For",
    "            End If",
    "        Next k",
    "        For k = 1 To p.TabStops.Count",
    "            If p.TabStops(k).Alignment = wdAlignTabRight Then",
    "                rechtsbuendig = rechtsbuendig + 1",
    "                Exit For",
    "            End If",
    "        Next k",
    "        For k = 1 To p.TabStops.Count",
    "            If p.TabStops(k).Alignment = wdAlignTabDecimal Then",
    "                dezimal = dezimal + 1",
    "                Exit For",
    "            End If",
    "        Next k",
    "    End If",
    "Next p",
    'Zahl "absaetzeMitTab", mitTab',
    'Zahl "absaetzeMitTabstopp", mitTabstopp',
    'Zahl "absaetzeMitFuellzeichen", mitFuellzeichen',
    'Zahl "absaetzeRechtsbuendig", rechtsbuendig',
    'Zahl "absaetzeDezimal", dezimal',
    'Zahl "absaetzeMitMehrfachTab", mehrfachTab',
  ],
});

/** Umbruchsteuerung der Überschrift 1: Seitenumbruch oberhalb statt von Hand. */
export const MAKRO_UMBRUCH = pruefmakro({
  pruefung: "word-umbruch",
  host: "Word",
  titel: "Umbruchsteuerung auslesen",
  rumpf: [
    "Dim f As ParagraphFormat, p As Paragraph",
    "Dim ueberschriften As Long, mitUmbruch As Long, vonHand As Long, zusammen As Long",
    "Set f = ActiveDocument.Styles(wdStyleHeading1).ParagraphFormat",
    'JaNein "vorlage.umbruchOberhalb", f.PageBreakBefore <> 0',
    'JaNein "vorlage.mitNaechstem", f.KeepWithNext <> 0',
    'JaNein "vorlage.zusammenhalten", f.KeepTogether <> 0',
    "For Each p In ActiveDocument.Paragraphs",
    "    If p.OutlineLevel = wdOutlineLevelBodyText And Len(p.Range.Text) > 1 Then",
    "        If p.Format.KeepWithNext <> 0 Or p.Format.KeepTogether <> 0 Then zusammen = zusammen + 1",
    "    End If",
    "    If p.OutlineLevel = wdOutlineLevel1 And Len(p.Range.Text) > 1 Then",
    "        ueberschriften = ueberschriften + 1",
    "        If p.Format.PageBreakBefore <> 0 Then mitUmbruch = mitUmbruch + 1",
    "        If InStr(p.Range.Text, Chr$(12)) > 0 Then",
    "            vonHand = vonHand + 1",
    "        ElseIf Not p.Previous Is Nothing Then",
    "            If InStr(p.Previous.Range.Text, Chr$(12)) > 0 Then vonHand = vonHand + 1",
    "        End If",
    "    End If",
    "Next p",
    'Zahl "ueberschriften", ueberschriften',
    'Zahl "ueberschriftenMitUmbruch", mitUmbruch',
    'Zahl "umbruecheVonHand", vonHand',
    'Zahl "fliesstextZusammengehalten", zusammen',
  ],
});

/** Seitenfarbe und Seitenrand aus der Registerkarte „Entwurf“. */
export const MAKRO_SEITENFARBE = pruefmakro({
  pruefung: "word-seitenfarbe",
  host: "Word",
  titel: "Seitenfarbe und Seitenrand auslesen",
  rumpf: [
    "Dim s As Section, rahmen As Boolean, sichtbar As Long, farbe As Long",
    'JaNein "hintergrundAnzeige", ActiveWindow.View.DisplayBackgrounds',
    "' Ohne Seitenfarbe liefert Word hier je nach Version einen Fehler.",
    "On Error Resume Next",
    "sichtbar = ActiveDocument.Background.Fill.Visible",
    "farbe = ActiveDocument.Background.Fill.ForeColor.RGB",
    "On Error GoTo Fehler",
    'JaNein "hintergrund", sichtbar <> 0',
    'Zahl "hintergrundFarbe", farbe',
    "For Each s In ActiveDocument.Sections",
    "    If s.Borders.Enable <> 0 Then rahmen = True",
    "Next s",
    'JaNein "seitenrahmen", rahmen',
  ],
});

/** Initialen (Einfügen → Initiale). */
export const MAKRO_INITIALE = pruefmakro({
  pruefung: "word-initiale",
  host: "Word",
  titel: "Initialen auslesen",
  rumpf: [
    "Dim p As Paragraph, n As Long, zeilen As Long",
    "For Each p In ActiveDocument.Paragraphs",
    "    If p.DropCap.Position <> wdDropNone Then",
    "        n = n + 1",
    "        If zeilen = 0 Then zeilen = p.DropCap.LinesToDrop",
    "    End If",
    "Next p",
    'Zahl "initialen", n',
    'Zahl "zeilen", zeilen',
  ],
});

/** SmartArt-Grafiken und Diagramme – beide kennt Office.js in Word nicht. */
export const MAKRO_GRAFIKEN = pruefmakro({
  pruefung: "word-grafiken",
  host: "Word",
  titel: "SmartArt und Diagramme auslesen",
  rumpf: [
    "Dim s As InlineShape, sh As Shape",
    "Dim smart As Long, knoten As Long, smartAlt As Long, diagramme As Long, titel As String",
    "For Each s In ActiveDocument.InlineShapes",
    "    If s.HasSmartArt Then",
    "        smart = smart + 1",
    "        If knoten = 0 Then knoten = s.SmartArt.AllNodes.Count",
    '        If Len(Trim$(s.AlternativeText)) > 0 Then smartAlt = smartAlt + 1',
    "    End If",
    "    If s.HasChart Then",
    "        diagramme = diagramme + 1",
    '        If titel = "" Then',
    "            If s.Chart.HasTitle Then titel = s.Chart.ChartTitle.Text",
    "        End If",
    "    End If",
    "Next s",
    "For Each sh In ActiveDocument.Shapes",
    "    If sh.HasSmartArt Then",
    "        smart = smart + 1",
    "        If knoten = 0 Then knoten = sh.SmartArt.AllNodes.Count",
    '        If Len(Trim$(sh.AlternativeText)) > 0 Then smartAlt = smartAlt + 1',
    "    End If",
    "    If sh.HasChart Then",
    "        diagramme = diagramme + 1",
    '        If titel = "" Then',
    "            If sh.Chart.HasTitle Then titel = sh.Chart.ChartTitle.Text",
    "        End If",
    "    End If",
    "Next sh",
    'Zahl "smartart", smart',
    'Zahl "smartartKnoten", knoten',
    'Zahl "smartartMitAlternativtext", smartAlt',
    'Zahl "diagramme", diagramme',
    'Wert "diagrammtitel", titel',
  ],
});

/** Sucht den Schnellbaustein „Kursbaustein“ in allen geladenen Vorlagen. */
export const MAKRO_BAUSTEINE = pruefmakro({
  pruefung: "word-bausteine",
  host: "Word",
  titel: "Schnellbausteine auslesen",
  rumpf: [
    "Dim t As Template, i As Long, gefunden As Boolean",
    "' Bausteinvorlagen werden erst beim ersten Zugriff geladen.",
    "On Error Resume Next",
    "Application.Templates.LoadBuildingBlocks",
    "On Error GoTo Fehler",
    "For Each t In Application.Templates",
    "    For i = 1 To t.BuildingBlockEntries.Count",
    '        If LCase$(t.BuildingBlockEntries(i).Name) = "kursbaustein" Then',
    "            gefunden = True",
    '            Wert "vorlage", t.Name',
    '            Wert "katalog", t.BuildingBlockEntries(i).Type.Name',
    '            Zahl "katalogNr", t.BuildingBlockEntries(i).Type.Index',
    '            Zahl "laenge", Len(t.BuildingBlockEntries(i).Value)',
    "        End If",
    "    Next i",
    "Next t",
    'JaNein "gefunden", gefunden',
  ],
});

/** Sucht den AutoKorrektur-Eintrag „#gruss“. */
export const MAKRO_AUTOKORREKTUR = pruefmakro({
  pruefung: "word-autokorrektur",
  host: "Word",
  titel: "AutoKorrektur auslesen",
  rumpf: [
    "Dim e As AutoCorrectEntry, gefunden As Boolean",
    "For Each e In Application.AutoCorrect.Entries",
    '    If LCase$(e.Name) = "#gruss" Then',
    "        gefunden = True",
    '        Wert "ersetzung", e.Value',
    '        JaNein "formatiert", e.RichText',
    "    End If",
    "Next e",
    'JaNein "gefunden", gefunden',
    'JaNein "ersetzenBeimTippen", Application.AutoCorrect.ReplaceText',
  ],
});

/** Seriendruck: Hauptdokumenttyp, Datenquelle, Filter und Felder. */
export const MAKRO_SERIENDRUCK = pruefmakro({
  pruefung: "word-seriendruck",
  host: "Word",
  titel: "Seriendruck auslesen",
  rumpf: [
    "With ActiveDocument.MailMerge",
    '    Zahl "typ", .MainDocumentType',
    '    Zahl "status", .State',
    '    Zahl "felder", .Fields.Count',
    "    If .State = wdMainAndDataSource Or .State = wdMainAndSourceAndHeader Then",
    '        JaNein "datenquelle", True',
    '        Wert "quelle", .DataSource.Name',
    '        Zahl "datensaetze", .DataSource.RecordCount',
    '        Zahl "datenfelder", .DataSource.DataFields.Count',
    '        Wert "abfrage", .DataSource.QueryString',
    "    Else",
    '        JaNein "datenquelle", False',
    "    End If",
    "End With",
  ],
});

/** VBA-Werte für `MailMerge.MainDocumentType`. */
export const SERIENDRUCK_TYP: Record<number, string> = {
  [-1]: "kein Seriendruck-Hauptdokument",
  0: "Briefe",
  1: "Etiketten",
  2: "Umschläge",
  3: "Verzeichnis",
  4: "E-Mail-Nachrichten",
  5: "Fax",
};

/** Liegt neben dem Dokument eine gleichnamige PDF-Datei? */
export const MAKRO_PDF = pruefmakro({
  pruefung: "word-pdf",
  host: "Word",
  titel: "PDF-Fassung suchen",
  rumpf: [
    "Dim basis As String, pdf As String, treffer As String",
    'If ActiveDocument.Path = "" Then',
    '    JaNein "gespeichert", False',
    "Else",
    '    JaNein "gespeichert", True',
    "    basis = ActiveDocument.FullName",
    '    basis = Left$(basis, InStrRev(basis, ".") - 1)',
    '    pdf = basis & ".pdf"',
    '    Wert "datei", pdf',
    "    ' Bei Dateien in OneDrive oder SharePoint ist der Pfad eine Webadresse.",
    "    On Error Resume Next",
    "    treffer = Dir$(pdf)",
    "    If Err.Number <> 0 Then",
    '        JaNein "pfadLesbar", False',
    "        Err.Clear",
    "    Else",
    '        JaNein "pfadLesbar", True',
    '        JaNein "pdf", Len(treffer) > 0',
    '        If Len(treffer) > 0 Then Wert "pdfZeit", Format$(FileDateTime(pdf), "yyyy-mm-dd\\Thh:nn:ss")',
    "    End If",
    "    On Error GoTo Fehler",
    "End If",
  ],
});

/**
 * Sucht ein aufgezeichnetes Makro „Kursmakro“ im Dokument und in der
 * Normal.dotm. Der Zugriff auf den VBA-Code setzt die Einstellung
 * „Zugriff auf das VBA-Projektobjektmodell vertrauen“ voraus.
 */
export const MAKRO_MAKROS = pruefmakro({
  pruefung: "word-makros",
  host: "Word",
  titel: "Aufgezeichnete Makros suchen",
  rumpf: [
    "Dim anzahl As Long, zugriff As Boolean, ort As String",
    'Wert "anwendung", Application.Name',
    "On Error Resume Next",
    "anzahl = ActiveDocument.VBProject.VBComponents.Count",
    "zugriff = (Err.Number = 0)",
    "Err.Clear",
    "On Error GoTo Fehler",
    'JaNein "vbZugriff", zugriff',
    "If zugriff Then",
    '    If ProjektHatKursmakro(ActiveDocument.VBProject) Then ort = "Dokument"',
    '    If ProjektHatKursmakro(NormalTemplate.VBProject) Then ort = "Normal.dotm"',
    "End If",
    'JaNein "kursmakro", Len(ort) > 0',
    'Wert "ort", ort',
  ],
  hilfsfunktionen: [
    `
Private Function ProjektHatKursmakro(ByVal projekt As Object) As Boolean
    ' Der Suchbegriff wird zusammengesetzt, damit dieses Prüfmakro sich nicht selbst findet.
    Dim komponente As Object, gesucht As String
    gesucht = "Sub " & "Kurs" & "makro"
    For Each komponente In projekt.VBComponents
        If komponente.CodeModule.CountOfLines > 0 Then
            If InStr(1, komponente.CodeModule.Lines(1, komponente.CodeModule.CountOfLines), _
                gesucht, vbTextCompare) > 0 Then ProjektHatKursmakro = True
        End If
    Next komponente
End Function`,
  ],
});
